import { useRoomQuery } from "@/modules/master/presentation/api/queries";
import { parseYearMonthFromDateYyyyMmDd } from "@/modules/reports/domain/lib/parse-year-month";
import { useInstructorPayrollBatchByMonthQuery } from "@/modules/reports/presentation/api/queries";
import { config, isDevMode } from "@/shared/lib/config";
import {
  addMinutesToTimeHhMm,
  formatDate,
  formatTime,
  getTodayYYYYMMDD,
  parseTimeToDate,
  toTimeHHmmss,
} from "@/shared/lib/date-time";
import { fetcher } from "@/shared/lib/fetcher";
import {
  PRIVATE_LESSON_DURATION_SELECT_OPTIONS,
  PRIVATE_LESSON_SLOT_DURATION_SET,
  formatSlotDurationLaoLabel,
  nearestSlotDurationMinutes,
  type PrivateLessonSlotDurationMinutes,
} from "@/shared/lib/private-lesson-slot-duration";
import {
  formatQuarterHhMm,
  isCanonicalQuarterHhMm,
  parseQuarterClock,
} from "@/shared/lib/quarter-time";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  hydrateLookupItems,
} from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { FormInfiniteMultiCombobox } from "@/shared/ui/FormInfiniteMultiCombobox";
import { QuarterHourMinuteSelect } from "@/shared/ui/QuarterHourMinuteSelect";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormDateField,
  FormInput,
  FormNumber,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { differenceInMinutes } from "date-fns";
import {
  AlertTriangleIcon,
  Calendar,
  ClockIcon,
  MapPinIcon,
  SparklesIcon,
  Timer,
  UserIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import type {
  CreateClassSessionDTO,
  RoomConflictResultDTO,
} from "../../domain/contracts/sessions";
import { classesApi } from "../api/client";
import { useClassTemplateQuery, useFindAlternativeRooms } from "../api/queries";
import {
  instructorsFromLeadAssist,
  leadAssistFromSessionInstructors,
} from "../lib/session-instructor-form";

type ClassTemplateItem = { id: string; name: string };
type RoomItem = { id: string; name: string };
type InstructorItem = { id: string; name: string };

function toTimeHHmmLocal(s: string | undefined): string {
  if (!s) return "09:30";
  if (s.length >= 8) return s.slice(0, 5);
  return s;
}

function toQuarterHhMmInitial(s: string | undefined): string {
  const raw = toTimeHHmmLocal(s);
  const { h, m } = parseQuarterClock(raw);
  return formatQuarterHhMm(h, m);
}

function inferSlotDurationMinutesFromRange(
  initialStart?: string,
  initialEnd?: string,
): PrivateLessonSlotDurationMinutes {
  if (!initialStart?.trim() || !initialEnd?.trim()) return 60;
  try {
    const mins = differenceInMinutes(
      parseTimeToDate(toQuarterHhMmInitial(initialEnd)),
      parseTimeToDate(toQuarterHhMmInitial(initialStart)),
    );
    if (mins <= 0) return 60;
    if (PRIVATE_LESSON_SLOT_DURATION_SET.has(mins))
      return mins as PrivateLessonSlotDurationMinutes;
    return nearestSlotDurationMinutes(mins);
  } catch {
    return 60;
  }
}

const slotDurationZ = z.union([
  z.literal(15),
  z.literal(30),
  z.literal(45),
  z.literal(60),
  z.literal(75),
  z.literal(90),
  z.literal(105),
  z.literal(120),
  z.literal(135),
  z.literal(150),
  z.literal(165),
  z.literal(180),
]);

const sessionLocationKindFieldZ = z.enum(["studio", "off_site"]);
const sessionEngagementKindFieldZ = z.enum([
  "standard",
  "partner_instructor_only",
]);

const ClassSessionFormSchema = z
  .object({
    templateId: z.string().min(1, "ຕ້ອງເລືອກ template"),
    date: z.string(),
    startTime: z.string().min(1, "ຕ້ອງເລືອກເວລາເລີ່ມຕົ້ນ"),
    slotDurationMinutes: slotDurationZ,
    locationKind: sessionLocationKindFieldZ,
    offSiteLabel: z.string().optional(),
    engagementKind: sessionEngagementKindFieldZ,
    roomId: z.string(),
    leadInstructorId: z.string().min(1, "ຕ້ອງເລືອກຄູຫຼັກ"),
    assistInstructorIds: z.array(z.string()).max(3),
    capacity: z
      .number()
      .int("ຄວາມຈຸຕ້ອງເປັນຈຳນວນເຕັມ")
      .positive("ຄວາມຈຸຕ້ອງເປັນຈຳນວນບວກ"),
    notes: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (!isCanonicalQuarterHhMm(data.startTime)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ເວລາເລີ່ມຕົ້ນຕ້ອງເປັນນາທີ 00 / 15 / 30 / 45",
        path: ["startTime"],
      });
    }
    if (
      data.locationKind === "studio" &&
      (!data.roomId || data.roomId.trim().length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງເລືອກຫ້ອງ",
        path: ["roomId"],
      });
    }
    if (
      data.locationKind === "off_site" &&
      data.roomId &&
      data.roomId.trim().length > 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຄລາດສ໌ນອກສະຖານທີ່ບໍ່ໃຊ້ຫ້ອງສະຕູດິໂອ — ລ້າງຫ້ອງຫຼືປ່ຽນເປັນສະຕູດິໂອ",
        path: ["roomId"],
      });
    }

    const lead = data.leadInstructorId.trim();
    const assists = data.assistInstructorIds ?? [];
    if (lead && assists.includes(lead)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຄູຫຼັກກັບຄູຊ່ວຍຕ້ອງບໍ່ຊ້ຳກັນ",
        path: ["assistInstructorIds"],
      });
    }
    if (
      data.engagementKind === "partner_instructor_only" &&
      assists.length > 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຄລາດສ໌ Partner ບໍ່ສາມາດເພີ່ມຄູຊ່ວຍ (TA)",
        path: ["assistInstructorIds"],
      });
    }
  });

const ClassSessionFormEditSchema = ClassSessionFormSchema.extend({
  changeReason: z.string().min(1, "ຕ້ອງໃສ່ເຫດຜົນການປ່ຽນແປງ"),
  status: z.enum(["scheduled", "completed", "cancelled"]),
});

export type ClassSessionFormValues = z.infer<typeof ClassSessionFormSchema> & {
  /** ສຳລັບໂໝດແກ້ໄຂເທົ່ານັ້ນ */
  changeReason?: string;
  status?: "scheduled" | "completed" | "cancelled";
};

type ClassSessionSubmitPayload = CreateClassSessionDTO & {
  changeReason?: string;
  status?: "scheduled" | "completed" | "cancelled";
};

type ClassSessionFormProps = {
  initialValues?: Partial<ClassSessionFormValues> & {
    endTime?: string;
    instructors?: Array<{ instructorId: string; role: string }>;
    instructorIds?: string[];
  };
  onSubmit: (values: ClassSessionSubmitPayload) => void;
  /** ບັນທຶກແລ້ວບໍ່ນຳທາງ ແລະບໍ່ລ້າງຟອມ (ສຳລັບສ້າງ/ແກ້ໄຂຕໍ່) */
  onSubmitAndStay?: (values: ClassSessionSubmitPayload) => void | Promise<void>;
  submitting?: boolean;
  isEdit?: boolean;
  sessionId?: string; // For excluding current ຊົ່ວໂມງຮຽນ when checking conflicts
  /** ເມື່ອຢູ່ໃນ dialog ສາມາດສົ່ງ onCancel ເພື່ອแสดงปุ่มຍົກເລີກ */
  onCancel?: () => void;
};

export function ClassSessionForm({
  initialValues,
  onSubmit,
  onSubmitAndStay,
  submitting,
  isEdit = false,
  sessionId,
  onCancel,
}: ClassSessionFormProps) {
  const methods = RHF.useForm<ClassSessionFormValues>({
    resolver: zodResolver(
      isEdit ? ClassSessionFormEditSchema : ClassSessionFormSchema,
    ),
    defaultValues: {
      templateId: initialValues?.templateId || "",
      date: initialValues?.date || getTodayYYYYMMDD(),
      startTime: toQuarterHhMmInitial(initialValues?.startTime || "09:30"),
      slotDurationMinutes: inferSlotDurationMinutesFromRange(
        initialValues?.startTime,
        initialValues?.endTime,
      ),
      locationKind: initialValues?.locationKind ?? "studio",
      offSiteLabel: initialValues?.offSiteLabel ?? "",
      engagementKind: initialValues?.engagementKind ?? "standard",
      roomId: initialValues?.roomId || "",
      ...(() => {
        const fromRoles = leadAssistFromSessionInstructors(
          initialValues?.instructors,
          initialValues?.instructorIds,
        );
        return {
          leadInstructorId:
            initialValues?.leadInstructorId || fromRoles.leadInstructorId,
          assistInstructorIds:
            initialValues?.assistInstructorIds ??
            fromRoles.assistInstructorIds,
        };
      })(),
      capacity: initialValues?.capacity ?? undefined,
      notes: initialValues?.notes || "",
      changeReason: initialValues?.changeReason || "",
      ...(isEdit
        ? {
          status: initialValues?.status ?? "scheduled",
        }
        : {}),
    },
  });

  const templateId = methods.watch("templateId");
  const date = methods.watch("date");
  const yearMonth = useMemo(() => {
    if (!isEdit || !date) return null;
    try {
      return parseYearMonthFromDateYyyyMmDd(date);
    } catch {
      return null;
    }
  }, [isEdit, date]);
  const payrollBatchQuery = useInstructorPayrollBatchByMonthQuery(
    yearMonth?.year ?? null,
    yearMonth?.month ?? null,
  );
  const payrollLocked = isEdit && !!payrollBatchQuery.data;
  const startTime = methods.watch("startTime");
  const slotDurationMinutes = methods.watch("slotDurationMinutes");
  const locationKind = methods.watch("locationKind");
  const roomId = methods.watch("roomId");
  const engagementKind = methods.watch("engagementKind");
  const leadInstructorId = methods.watch("leadInstructorId") ?? "";
  const assistInstructorIds = methods.watch("assistInstructorIds") ?? [];
  const instructorIds = [
    leadInstructorId,
    ...assistInstructorIds,
  ].filter(Boolean);

  useEffect(() => {
    if (
      engagementKind === "partner_instructor_only" &&
      assistInstructorIds.length > 0
    ) {
      methods.setValue("assistInstructorIds", []);
    }
  }, [engagementKind, assistInstructorIds.length, methods]);

  const capacity = methods.watch("capacity");
  const offSiteLabelWatch = methods.watch("offSiteLabel");
  const { data: template } = useClassTemplateQuery(templateId || "");
  const { data: room } = useRoomQuery(roomId || "");
  const { data: instructors } = useQuery({
    queryKey: ["master", "instructors", "byIds", instructorIds],
    queryFn: () =>
      hydrateLookupItems<InstructorItem>(
        `${config.apiUrl}/master/instructors/lookup`,
        instructorIds,
      ),
    enabled: instructorIds.length > 0,
  });

  // Conflict detection state
  const [conflictResult, setConflictResult] =
    useState<RoomConflictResultDTO | null>(null);
  const [isCheckingConflict, setIsCheckingConflict] = useState(false);

  // Alternative rooms state
  const findAlternatives = useFindAlternativeRooms();
  const [showAlternatives, setShowAlternatives] = useState(false);

  // Track previous roomId to detect actual changes
  const prevRoomIdRef = useRef<string | undefined>(initialValues?.roomId);

  const templateDurationMinutes =
    template && typeof template.duration === "number" ? template.duration : 0;

  const effectiveDurationMinutes = useMemo(() => {
    if (templateDurationMinutes > 0) return templateDurationMinutes;
    return slotDurationMinutes;
  }, [templateDurationMinutes, slotDurationMinutes]);

  const computedEndTime = useMemo(() => {
    if (!startTime?.trim()) return "";
    try {
      return addMinutesToTimeHhMm(startTime, effectiveDurationMinutes);
    } catch {
      return "";
    }
  }, [startTime, effectiveDurationMinutes]);

  const buildCreateDto = (
    form: ClassSessionFormValues,
  ): CreateClassSessionDTO => {
    const dur =
      templateDurationMinutes > 0
        ? templateDurationMinutes
        : form.slotDurationMinutes;
    const endHm = addMinutesToTimeHhMm(form.startTime, dur);
    return {
      templateId: form.templateId,
      date: form.date,
      startTime: toTimeHHmmss(form.startTime),
      endTime: toTimeHHmmss(endHm),
      locationKind: form.locationKind,
      engagementKind: form.engagementKind,
      offSiteLabel:
        form.locationKind === "off_site"
          ? form.offSiteLabel?.trim() || null
          : null,
      roomId: form.locationKind === "studio" ? form.roomId.trim() : undefined,
      instructors: instructorsFromLeadAssist(
        form.leadInstructorId,
        form.engagementKind === "partner_instructor_only"
          ? []
          : (form.assistInstructorIds ?? []),
      ),
      capacity: form.capacity,
      notes: form.notes ?? undefined,
    };
  };

  // Off-site: ບໍ່ໃຊ້ຫ້ອງ — ລ້າງ roomId ເມື່ອປ່ຽນປະເພດສະຖານທີ່
  useEffect(() => {
    if (locationKind === "off_site" && roomId && roomId.trim().length > 0) {
      methods.setValue("roomId", "");
    }
  }, [locationKind, roomId, methods]);

  // Auto-fill capacity from room when room changes (ສະຕູດິໂອເທົ່ານັ້ນ)
  useEffect(() => {
    if (locationKind !== "studio") return;
    // Only auto-fill if roomId actually changed (not on initial mount)
    const roomIdChanged = prevRoomIdRef.current !== roomId;
    if (room && room.capacity != null && roomId && roomIdChanged) {
      methods.setValue("capacity", room.capacity);
    }
    prevRoomIdRef.current = roomId;
  }, [room, roomId, methods]);

  // Check conflict only on submit
  const checkConflictOnSubmit = async (dto: CreateClassSessionDTO) => {
    setIsCheckingConflict(true);
    try {
      const result = await classesApi.sessions.checkConflict({
        roomId:
          dto.locationKind === "off_site"
            ? dto.roomId?.trim() || undefined
            : (dto.roomId ?? ""),
        date: dto.date,
        startTime: dto.startTime,
        endTime: dto.endTime,
        sessionLocationKind: dto.locationKind,
        instructorIds: dto.instructors?.length
          ? dto.instructors.map((i) => i.instructorId)
          : dto.instructorIds?.length
            ? dto.instructorIds
            : undefined,
        excludeSessionId: isEdit && sessionId ? sessionId : undefined,
      });
      return result;
    } catch (error) {
      console.error("Failed to check conflict:", error);
      return {
        hasConflict: false,
        conflictingSessions: [],
        busyInstructors: [],
      };
    } finally {
      setIsCheckingConflict(false);
    }
  };

  const handleSubmit = async (values: ClassSessionFormValues) => {
    const dto = buildCreateDto(values);
    const result = await checkConflictOnSubmit(dto);
    if (result.hasConflict) {
      setConflictResult(result);
      const suggestRooms =
        dto.locationKind !== "off_site" &&
        result.conflictingSessions.length > 0;
      if (suggestRooms) {
        try {
          await findAlternatives.mutateAsync({
            date: dto.date,
            startTime: dto.startTime,
            endTime: dto.endTime,
            excludeRoomId: dto.roomId,
            minCapacity: dto.capacity ?? undefined,
          });
          setShowAlternatives(true);
        } catch (error) {
          console.error("Failed to find alternatives:", error);
        }
      }
      return;
    }
    setConflictResult(null);
    setShowAlternatives(false);
    const payload: ClassSessionSubmitPayload = {
      ...dto,
      changeReason: values.changeReason,
      ...(isEdit ? { status: values.status } : {}),
    };
    onSubmit(payload);
  };

  const handleSubmitAndStay = async (values: ClassSessionFormValues) => {
    const dto = buildCreateDto(values);
    const result = await checkConflictOnSubmit(dto);
    if (result.hasConflict) {
      setConflictResult(result);
      const suggestRooms =
        dto.locationKind !== "off_site" &&
        result.conflictingSessions.length > 0;
      if (suggestRooms) {
        try {
          await findAlternatives.mutateAsync({
            date: dto.date,
            startTime: dto.startTime,
            endTime: dto.endTime,
            excludeRoomId: dto.roomId,
            minCapacity: dto.capacity ?? undefined,
          });
          setShowAlternatives(true);
        } catch (error) {
          console.error("Failed to find alternatives:", error);
        }
      }
      return;
    }
    setConflictResult(null);
    setShowAlternatives(false);
    const payload: ClassSessionSubmitPayload = {
      ...dto,
      changeReason: values.changeReason,
      ...(isEdit ? { status: values.status } : {}),
    };
    await onSubmitAndStay?.(payload);
  };

  const durationLabel = useMemo(
    () => formatSlotDurationLaoLabel(effectiveDurationMinutes),
    [effectiveDurationMinutes],
  );

  // Handle auto-suggest alternative rooms
  const handleFindAlternatives = async () => {
    if (locationKind === "off_site") return;
    if (!date || !startTime?.trim() || !computedEndTime) return;

    try {
      await findAlternatives.mutateAsync({
        date,
        startTime: toTimeHHmmss(startTime),
        endTime: toTimeHHmmss(computedEndTime),
        excludeRoomId: roomId,
        minCapacity: capacity ?? undefined,
      });
      setShowAlternatives(true);
    } catch (error) {
      console.error("Failed to find alternatives:", error);
    }
  };

  const handleSelectAlternativeRoom = (altRoomId: string) => {
    methods.setValue("roomId", altRoomId);
    setShowAlternatives(false);
    setConflictResult(null);
  };

  const roomName = room?.name || "";
  const placeSummary =
    locationKind === "off_site"
      ? offSiteLabelWatch?.trim() || "ນອກສະຖານທີ່"
      : roomName;
  const instructorNames = instructors?.map((i) => i.name).join(", ") || "";

  const handleAutoFill = () => {
    methods.reset({
      templateId: "",
      date: "",
      startTime: "",
      slotDurationMinutes: 60,
      locationKind: "studio",
      offSiteLabel: "",
      engagementKind: "standard",
      roomId: "",
      leadInstructorId: "",
      assistInstructorIds: [],
      capacity: 20,
      notes: "",
      changeReason: "",
      ...(isEdit ? { status: "scheduled" as const } : {}),
    });
    setConflictResult(null);
    setShowAlternatives(false);
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* Left Column - Form (2/3 width) */}
      <div className="lg:col-span-2">
        <FormRoot
          methods={methods}
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Template & Schedule Section */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">ແມ່ແບບ ແລະ ຕາຕະລາງ</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormInfiniteCombobox<ClassTemplateItem>
                name="templateId"
                label="ແມ່ແບບ"
                placeholder="ເລືອກ template"
                requiredMark
                queryKey={["classes", "templates", "lookup"]}
                queryFn={async ({ search, pageParam }) => {
                  const limit = 20;
                  const skip = (pageParam - 1) * limit;
                  const url = new URL(
                    `${config.apiUrl}/classes/templates/lookup`,
                  );
                  url.searchParams.set("limit", String(limit));
                  url.searchParams.set("skip", String(skip));
                  if (search) url.searchParams.set("q", search);
                  const data = await fetcher.get<{
                    items: Array<{
                      id: string;
                      name: string;
                      classTypeName: string;
                      levelName: string;
                    }>;
                    total: number;
                  }>(url.toString());
                  return {
                    items: data.items.map((t) => ({
                      id: t.id,
                      name: t.name,
                    })),
                    nextPage: skip + limit < data.total ? pageParam + 1 : null,
                  };
                }}
                preloadQueryFn={async (id) => {
                  const t = await fetcher.get<{
                    id: string;
                    classTypeName: string;
                    levelName: string;
                  }>(`${config.apiUrl}/classes/templates/${id}`);
                  return {
                    id: t.id,
                    name: `${t.classTypeName} - ${t.levelName}`,
                  };
                }}
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
              />

              <FormDateField name="date" label="ວັນທີ" requiredMark />

              <div className="grid grid-cols-1 gap-4 md:col-span-2 lg:grid-cols-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-1 font-medium text-sm leading-none">
                    <span>ເວລາເລີ່ມຕົ້ນ</span>
                    <span aria-hidden className="text-red-500">
                      *
                    </span>
                  </div>
                  <RHF.Controller
                    name="startTime"
                    control={methods.control}
                    render={({ field, fieldState }) => (
                      <>
                        <QuarterHourMinuteSelect
                          value={field.value ?? ""}
                          onChange={field.onChange}
                        />
                        {fieldState.error?.message ? (
                          <p className="text-red-500 text-xs">
                            {fieldState.error.message}
                          </p>
                        ) : null}
                      </>
                    )}
                  />
                </div>
                <div className="space-y-2">
                  {templateDurationMinutes > 0 ? (
                    <>
                      <div className="font-medium text-muted-foreground text-sm leading-none">
                        ຄວາມຍາວ
                      </div>
                      <div className="flex min-h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-muted-foreground text-sm">
                        {templateDurationMinutes} ນາທີ (ກົງແມ່ແບບ)
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-1 font-medium text-sm leading-none">
                        <span>ຄວາມຍາວ (ນາທີ)</span>
                        <span aria-hidden className="text-red-500">
                          *
                        </span>
                      </div>
                      <RHF.Controller
                        name="slotDurationMinutes"
                        control={methods.control}
                        render={({ field, fieldState }) => {
                          const raw = Number(field.value);
                          const safe = PRIVATE_LESSON_SLOT_DURATION_SET.has(raw)
                            ? raw
                            : 60;
                          return (
                            <>
                              <SimpleSelect
                                value={String(safe)}
                                onValueChange={(val) => {
                                  const m = Number(val);
                                  if (!PRIVATE_LESSON_SLOT_DURATION_SET.has(m))
                                    return;
                                  field.onChange(m);
                                }}
                                options={PRIVATE_LESSON_DURATION_SELECT_OPTIONS}
                                className="h-9 w-full"
                                placeholder="ເລືອກຄວາມຍາວ"
                              />
                              {fieldState.error?.message ? (
                                <p className="text-red-500 text-xs">
                                  {fieldState.error.message}
                                </p>
                              ) : null}
                            </>
                          );
                        }}
                      />
                    </>
                  )}
                </div>
                <div className="space-y-2 lg:col-span-2">
                  <div className="font-medium text-muted-foreground text-sm leading-none">
                    ເວລາສິ້ນສຸດ <span className="text-xs">(ຄຳນວນອັດຕະໂນມັດ)</span>
                  </div>
                  <div className="flex h-9 items-center rounded-md border border-input border-dashed bg-muted/50 px-3 text-muted-foreground text-sm">
                    {computedEndTime || "—"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">ສະຖານທີ່ ແລະ ປະເພດໂຄງການ</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <RHF.Controller
                name="locationKind"
                control={methods.control}
                render={({ field, fieldState }) => (
                  <div className="space-y-2">
                    <div className="flex items-center gap-1 font-medium text-sm leading-none">
                      <span>ປະເພດສະຖານທີ່</span>
                      <span aria-hidden className="text-red-500">
                        *
                      </span>
                    </div>
                    <SimpleSelect
                      value={field.value}
                      onValueChange={(v) =>
                        field.onChange(v as "studio" | "off_site")
                      }
                      options={[
                        { value: "studio", label: "ສະຕູດິໂອ" },
                        { value: "off_site", label: "ນອກສະຖານທີ່" },
                      ]}
                      className="h-9 w-full"
                    />
                    {fieldState.error?.message ? (
                      <p className="text-red-500 text-xs">
                        {fieldState.error.message}
                      </p>
                    ) : null}
                  </div>
                )}
              />
              {locationKind === "off_site" ? (
                <FormInput
                  name="offSiteLabel"
                  label="ຊື່ສະຖານທີ່ (ທາງເລືອກ)"
                  placeholder="ຕົວຢ່າງ: ໂຮງຮຽນ ມປລ — ຫ້ອງຟິດເນສ"
                />
              ) : (
                <div className="hidden md:block" aria-hidden />
              )}
              <RHF.Controller
                name="engagementKind"
                control={methods.control}
                render={({ field }) => (
                  <div className="space-y-2 md:col-span-2">
                    <div className="flex items-center gap-1 font-medium text-sm leading-none">
                      <span>ປະເພດໂຄງການ</span>
                      <span aria-hidden className="text-red-500">
                        *
                      </span>
                    </div>
                    <SimpleSelect
                      value={field.value}
                      onValueChange={(v) =>
                        field.onChange(
                          v as "standard" | "partner_instructor_only",
                        )
                      }
                      options={[
                        { value: "standard", label: "ມາດຕະຖານ" },
                        {
                          value: "partner_instructor_only",
                          label: "Partner (instructor-only)",
                        },
                      ]}
                      className="h-9 w-full"
                    />
                    <p className="text-muted-foreground text-xs">
                      ໂຄງການ Partner: ປິດການເຊັກຊື່ນັກຮຽນຜູ້ມາສິດແພັກເກດ — ໃຊ້ການເຊັກຊື່ຄູຄືເກົ່າ.
                    </p>
                  </div>
                )}
              />
            </div>
          </div>

          {isEdit && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">ສະຖານະຊົ່ວໂມງຮຽນ</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <RHF.Controller
                  name="status"
                  control={methods.control}
                  render={({ field, fieldState }) => (
                    <div className="space-y-2">
                      <div className="flex items-center gap-1 font-medium text-sm leading-none">
                        <span>ສະຖານະ</span>
                        <span aria-hidden className="text-red-500">
                          *
                        </span>
                      </div>
                      <SimpleSelect
                        value={field.value}
                        onValueChange={(v) =>
                          field.onChange(
                            v as "scheduled" | "completed" | "cancelled",
                          )
                        }
                        options={[
                          { value: "scheduled", label: "ຈອງແລ້ວ" },
                          { value: "completed", label: "ເຂົ້າຮຽນແລ້ວ" },
                          { value: "cancelled", label: "ຍົກເລີກ" },
                        ]}
                        className="h-9 w-full"
                      />
                      {fieldState.error?.message ? (
                        <p className="text-red-500 text-xs">
                          {fieldState.error.message}
                        </p>
                      ) : null}
                    </div>
                  )}
                />
              </div>
            </div>
          )}

          {/* Assignment Section */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">ການມອບໝາຍ</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {locationKind === "studio" ? (
                <div>
                  <FormInfiniteCombobox<RoomItem>
                    name="roomId"
                    label="ຫ້ອງ"
                    placeholder="ເລືອກຫ້ອງ"
                    requiredMark
                    queryKey={["master", "rooms", "lookup"]}
                    queryFn={({ search, pageParam }) =>
                      fetchLookupForInfinite<RoomItem>(
                        `${config.apiUrl}/master/rooms/lookup`,
                        { search, pageParam },
                      )
                    }
                    preloadQueryFn={(id) =>
                      hydrateLookupItem<RoomItem>(
                        `${config.apiUrl}/master/rooms/lookup`,
                        id,
                      ).then((item) => item ?? null)
                    }
                    getLabel={(item) => item.name}
                    getValue={(item) => item.id}
                  />
                  {room && room.capacity != null && (
                    <p className="mt-1 text-muted-foreground text-xs">
                      ຄວາມຈຸ: {room.capacity} ຄົນສູງສຸດ
                    </p>
                  )}
                  {room &&
                    room.capacity != null &&
                    capacity != null &&
                    capacity > room.capacity && (
                      <Alert className="mt-2">
                        <AlertDescription className="text-red-600">
                          ຄວາມຈຸຂອງຊົ່ວໂມງຮຽນ ({capacity}) ເກີນຄວາມຈຸຂອງຫ້ອງ (
                          {room.capacity})
                        </AlertDescription>
                      </Alert>
                    )}
                </div>
              ) : (
                <div className="rounded-md border border-dashed bg-muted/30 p-4 text-muted-foreground text-sm">
                  ຄລາດສ໌ນອກສະຖານທີ່ບໍ່ຜູກຫ້ອງສະຕູດິໂອ
                </div>
              )}

              {payrollLocked ? (
                <p className="md:col-span-2 text-amber-800 text-sm dark:text-amber-200">
                  ປິດງວດ payroll ແລ້ວ — ບໍ່ສາມາດແກ້ວຄູຫຼັກ/TA
                </p>
              ) : null}
              <fieldset disabled={payrollLocked} className="contents">
                <FormInfiniteCombobox<InstructorItem>
                  name="leadInstructorId"
                  label="ຄູຫຼັກ"
                  placeholder="ເລືອກຄູຫຼັກ"
                  requiredMark
                  queryKey={["master", "instructors", "lookup", "lead"]}
                  queryFn={({ search, pageParam }) =>
                    fetchLookupForInfinite<InstructorItem>(
                      `${config.apiUrl}/master/instructors/lookup`,
                      { search, pageParam },
                    )
                  }
                  preloadQueryFn={(id) =>
                    hydrateLookupItem<InstructorItem>(
                      `${config.apiUrl}/master/instructors/lookup`,
                      id,
                    )
                  }
                  getLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  clearable
                />

                {engagementKind !== "partner_instructor_only" ? (
                  <div className="space-y-1">
                    <FormInfiniteMultiCombobox<InstructorItem>
                      name="assistInstructorIds"
                      label="ຄູຊ່ວຍ (TA)"
                      placeholder="ເລືອກຄູຊ່ວຍ ສູງສຸດ 3 ຄົນ"
                      queryKey={["master", "instructors", "lookup", "assist"]}
                      queryFn={({ search, pageParam }) =>
                        fetchLookupForInfinite<InstructorItem>(
                          `${config.apiUrl}/master/instructors/lookup`,
                          { search, pageParam },
                        )
                      }
                      preloadQueryFn={(ids) =>
                        hydrateLookupItems<InstructorItem>(
                          `${config.apiUrl}/master/instructors/lookup`,
                          ids,
                        )
                      }
                      getLabel={(item) => item.name}
                      getValue={(item) => item.id}
                    />
                    <p className="text-muted-foreground text-xs">ຄິດຄ່າຈ້າງຕໍ່ຄັ້ງ · ສູງສຸດ 3 ຄົນ · ບໍ່ຊ້ຳກັບຄູຫຼັກ</p>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">ຄລາດສ໌ Partner ໃຊ້ຄູຫຼັກຄົນດຽວ — ບໍ່ມີຄູຊ່ວຍ (TA)</p>
                )}
              </fieldset>
            </div>
          </div>


          {/* Settings Section */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">ການຕັ້ງຄ່າ</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormNumber
                name="capacity"
                label="ຄວາມຈຸ"
                placeholder="ປ້ອນຄວາມຈຸ"
                requiredMark
              />
            </div>

            <FormTextarea
              name="notes"
              label="ໝາຍເຫດ"
              placeholder="ປ້ອນໝາຍເຫດ (ບໍ່ບັງຄັບ)"
              rows={3}
            />
          </div>

          {isEdit && (
            <FormTextarea
              name="changeReason"
              label="ເຫດຜົນການປ່ຽນແປງ"
              requiredMark
              placeholder="ປ້ອນເຫດຜົນການປ່ຽນແປງ (ບັງຄັບເມື່ອມີການປ່ຽນແປງ)"
              rows={2}
            />
          )}

          <div className="flex justify-end gap-2 border-t pt-4">
            {isDevMode && (
              <Button
                type="button"
                variant="outline"
                onClick={handleAutoFill}
                disabled={submitting}
              >
                <SparklesIcon className="size-4" />
                ຕື່ມອັດຕະໂນມັດ
              </Button>
            )}
            {onCancel != null && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={submitting}
              >
                ຍົກເລີກ
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => methods.reset()}
              disabled={submitting}
            >
              ລ້າງ
            </Button>
            {onSubmitAndStay && (
              <Button
                type="button"
                variant="secondary"
                disabled={submitting || isCheckingConflict}
                onClick={() => methods.handleSubmit(handleSubmitAndStay)()}
              >
                {isCheckingConflict
                  ? "ກຳລັງກວດສອບ..."
                  : submitting
                    ? "ກຳລັງບັນທຶກ..."
                    : "ບັນທຶກແລະຢູ່ຕໍ່"}
              </Button>
            )}
            <Button type="submit" disabled={submitting || isCheckingConflict}>
              {isCheckingConflict
                ? "ກຳລັງກວດສອບ..."
                : submitting
                  ? "ກຳລັງບັນທຶກ..."
                  : "ບັນທຶກ"}
            </Button>
          </div>
        </FormRoot>
      </div>

      {/* Right Column - Conflict Detection & Summary (1/3 width) */}
      <div className="lg:col-span-1">
        <div className="space-y-6">
          {/* Conflict Warning */}
          {conflictResult?.hasConflict && (
            <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertTriangleIcon className="size-5 text-red-600" />
                  <CardTitle className="text-red-900 dark:text-red-100">
                    ພົບການຂັດແຍ້ງ
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <AlertDescription className="text-red-900 dark:text-red-100">
                  {locationKind === "studio" &&
                    conflictResult.conflictingSessions.length > 0 ? (
                    <>
                      ຫ້ອງທີ່ເລືອກ <strong>{roomName}</strong>{" "}
                      ກຳລັງຖືກໃຊ້ງານໃນເວລາທີ່ຮ້ອງຂໍ.
                    </>
                  ) : null}
                  {conflictResult.busyInstructors &&
                    conflictResult.busyInstructors.length > 0 ? (
                    <>
                      {locationKind === "studio" &&
                        conflictResult.conflictingSessions.length > 0 &&
                        " "}
                      ຄູທີ່ເລືອກບໍ່ວ່າງ:{" "}
                      <strong>
                        {conflictResult.busyInstructors
                          .map((i) => i.name)
                          .join(", ")}
                      </strong>
                    </>
                  ) : null}
                </AlertDescription>
                {conflictResult.busyInstructors &&
                  conflictResult.busyInstructors.length > 0 ? (
                  <div className="space-y-2">
                    <p className="font-semibold text-red-900 text-sm uppercase dark:text-red-100">
                      ຄູທີ່ບໍ່ວ່າງໃນເວລານີ້
                    </p>
                    <ul className="list-inside list-disc space-y-1 text-red-900 text-sm dark:text-red-100">
                      {conflictResult.busyInstructors.map((i) => (
                        <li key={i.id}>{i.name}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {conflictResult.conflictingSessions.length > 0 ? (
                  <div className="space-y-2">
                    <p className="font-semibold text-red-900 text-sm uppercase dark:text-red-100">
                      ຊົ່ວໂມງຮຽນທີ່ຊ້ຳກັນ
                    </p>
                    {conflictResult.conflictingSessions.map((session) => (
                      <div
                        key={session.id}
                        className="rounded-lg border border-red-200 bg-white p-3 dark:bg-gray-900"
                      >
                        <p className="font-semibold">{session.templateName}</p>
                        <div className="mt-1 flex items-center gap-2 text-muted-foreground text-xs">
                          <ClockIcon className="size-3" />
                          <span>
                            {formatTime(session.startTime)} -{" "}
                            {formatTime(session.endTime)}
                          </span>
                        </div>
                        {Array.isArray(session.instructorNames) &&
                          session.instructorNames.length > 0 && (
                            <div className="mt-1 flex items-center gap-2 text-muted-foreground text-xs">
                              <UserIcon className="size-3" />
                              <span>
                                ຄູຜູ້ສອນ: {session.instructorNames.join(", ")}
                              </span>
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                ) : null}

                <Button
                  variant="outline"
                  className="w-full border-red-300 text-red-700 hover:bg-red-100 dark:text-red-300"
                  onClick={handleFindAlternatives}
                  disabled={
                    locationKind === "off_site" ||
                    findAlternatives.isPending ||
                    isCheckingConflict
                  }
                >
                  {findAlternatives.isPending
                    ? "ກຳລັງຄົ້ນຫາ..."
                    : "ແນະນຳຫ້ອງທາງເລືອກ"}
                </Button>

                {showAlternatives &&
                  findAlternatives.data?.rooms &&
                  findAlternatives.data.rooms.length > 0 && (
                    <div className="space-y-2">
                      <p className="font-semibold text-sm">ຫ້ອງທີ່ມີຢູ່:</p>
                      {findAlternatives.data.rooms.map((altRoom) => (
                        <Button
                          key={altRoom.id}
                          variant="outline"
                          className="w-full justify-start"
                          onClick={() =>
                            handleSelectAlternativeRoom(altRoom.id)
                          }
                        >
                          <MapPinIcon className="mr-2 size-4" />
                          {altRoom.name}
                          {altRoom.capacity != null &&
                            ` (${altRoom.capacity} ຄົນ)`}
                        </Button>
                      ))}
                    </div>
                  )}

                {showAlternatives &&
                  findAlternatives.data?.rooms &&
                  findAlternatives.data.rooms.length === 0 && (
                    <Alert>
                      <AlertDescription>
                        ບໍ່ມີຫ້ອງທາງເລືອກທີ່ມີຢູ່ສຳລັບເວລານີ້.
                      </AlertDescription>
                    </Alert>
                  )}
              </CardContent>
            </Card>
          )}

          {/* Summary */}
          <Card className="border-border">
            <CardContent className="space-y-4">
              <CardTitle className="font-semibold text-base">ສະຫຼຸບ</CardTitle>
              {/* Schedule Section */}
              <div className="space-y-3">
                {date && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <Calendar className="size-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        ວັນທີ
                      </p>
                      <p className="mt-0.5 font-semibold text-sm">
                        {formatDate(date)}
                      </p>
                    </div>
                  </div>
                )}

                {startTime && computedEndTime && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <ClockIcon className="size-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        ເວລາ
                      </p>
                      <p className="mt-0.5 font-semibold text-sm">
                        {formatTime(startTime)} - {formatTime(computedEndTime)}
                      </p>
                    </div>
                  </div>
                )}

                {durationLabel && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <Timer className="size-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        ໄລຍະເວລາ
                      </p>
                      <p className="mt-0.5 font-semibold text-sm">
                        {durationLabel}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Resources Section */}
              {(placeSummary || instructorIds.length > 0) && (
                <div className="border-border border-t pt-4">
                  <p className="mb-3 text-muted-foreground text-xs uppercase tracking-wide">
                    ຊັບພະຍາກອນ
                  </p>
                  <div className="space-y-3">
                    {placeSummary ? (
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                          <MapPinIcon className="size-4 text-muted-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-muted-foreground text-xs uppercase tracking-wide">
                            {locationKind === "off_site" ? "ສະຖານທີ່" : "ຫ້ອງ"}
                          </p>
                          <p className="mt-0.5 font-semibold text-sm">
                            {placeSummary}
                          </p>
                          {locationKind === "studio" &&
                            room &&
                            room.capacity != null && (
                              <p className="mt-1 text-muted-foreground text-xs">
                                ຄວາມຈຸ: {room.capacity} ຄົນ
                              </p>
                            )}
                        </div>
                      </div>
                    ) : null}

                    {instructorIds.length > 0 ? (
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                          <UserIcon className="size-4 text-muted-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-muted-foreground text-xs uppercase tracking-wide">
                            ຄູຜູ້ສອນ
                          </p>
                          <p className="mt-0.5 font-semibold text-sm">
                            {instructorNames || "-"}
                          </p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!date &&
                !startTime &&
                !placeSummary &&
                instructorIds.length === 0 && (
                  <div className="py-8 text-center">
                    <p className="text-muted-foreground text-sm">
                      ຕື່ມຂໍ້ມູນໃນຟອມເພື່ອເບິ່ງສະຫຼຸບ
                    </p>
                  </div>
                )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
